import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { ImageUp, RotateCcw, Check, PackageOpen, Trash2, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Copy } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

// iter 112: Quotation-scoped product editor (opens from the pencil icon on
// each line in a quote). All edits live under `cart.product_overrides` and
// are scoped to THIS quote only - the global product record and other quotes
// are unaffected. Variant-level fields (UPC / carton dims / factory cost) are
// shown read-only for reference; per-variant overrides are a future phase.
export default function QuoteItemOverrideDialog({ open, onOpenChange, cartId, item, cartItems, onSaved, productList, onNavigate }) {
  const [form, setForm] = useState({
    description: "",
    specification: "",
    // iter 125: 中文产品描述 - separate override field below spec.
    chinese_name: "",
    // iter 227 (2026-02): 包装要求 / 品质要求 quote-only overrides.
    packaging_requirement: "",
    quality_requirement: "",
    remark: "",
    // iter 117: the LIST shows `item_no` (公司产品编号 e.g. "DT02607") as
    // the "product code", so this dialog binds the same field. Previously
    // it was `customer_product_no` (客户产品编号), which made the field
    // appear empty even though the row clearly showed a code.
    // iter 118: show BOTH company code (item_no) and customer code
    // (customer_product_no) as separate editable fields - they represent
    // two different identifiers a merchandiser needs to see side by side.
    item_no: "",
    customer_product_no: "",
    customs_duty_rate: "",
    // iter 184 (2026-02): 退税率 - product-level quote-only override.
    tax_rebate_rate: "",
    // iter 185 (2026-02): 压缩率 - product-level qty_40ft divisor.
    compression_rate: "",
  });
  const [entryOverrides, setEntryOverrides] = useState({});
  // iter 192 (2026-02): live-preview computed prices keyed by
  // price_entry_id. Populated by a debounced POST to
  // /carts/{id}/product-overrides/{pid}/preview whenever the user edits
  // Duty / Rebate / 压缩率 / any variant field. Falls back to the
  // server-persisted `v.price` when the entry hasn't been previewed yet.
  const [previewPrices, setPreviewPrices] = useState({});
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  // iter 239 (2026-02): 自动保存 —— 用户编辑 form / entryOverrides 后
  // 防抖 800ms 静默 PUT。切换 Prev/Next 或关闭 Dialog 时立即 flush。
  const autoSaveTimerRef = useRef(null);
  const initializingRef = useRef(true);
  const latestPayloadRef = useRef(null);
  const performAutoSaveRef = useRef(null);
  const [autoSavedAt, setAutoSavedAt] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);
  // iter 114: Similar Images grid (below the photo). Reuses the
  // /inventory/similar endpoint - fuzzy match by item_no OR description
  // token, both fields probed.
  const [similarSearch, setSimilarSearch] = useState("");
  const [similarItems, setSimilarItems] = useState([]);
  // iter 114 · 导入订单产品资料: dialog state.
  const [importOpen, setImportOpen] = useState(false);
  const [importForEntryId, setImportForEntryId] = useState(null);
  const [importSearch, setImportSearch] = useState("");
  const [importResults, setImportResults] = useState([]);
  const [importCopyPrice, setImportCopyPrice] = useState(false);
  // iter 115: inline "Add Size" form state. Only opens when the user clicks
  // "+ 添加尺寸"; POST fires on Save; Cancel resets.
  const [addSizeOpen, setAddSizeOpen] = useState(false);
  const [addSizeForm, setAddSizeForm] = useState({
    size: "", upc: "", purchase_price: "", pcs_per_ctn: "",
    carton_length: "", carton_width: "", carton_height: "",
    gross_weight: "", qty: "",
  });
  const [addSizeSaving, setAddSizeSaving] = useState(false);
  // iter 144: compact "similar photos" - only show first 6 by default,
  // toggle with 查看全部 / View All link (per mockup).
  const [showAllSimilar, setShowAllSimilar] = useState(false);

  const productVariants = (cartItems || []).filter((ci) => ci.product_id === item?.product_id);

  useEffect(() => {
    if (item) {
      // iter 239: mark initializing so the auto-save effect below skips
      // this synthetic form population when the dialog opens or Prev/Next
      // switches to a new product. Cleared on the next microtask, after
      // React has flushed the setState calls below.
      initializingRef.current = true;
      setForm({
        description: item.description || "",
        specification: item.specification || "",
        chinese_name: item.chinese_name || "",
        packaging_requirement: item.packaging_requirement || "",
        quality_requirement: item.quality_requirement || "",
        remark: item.remark || "",
        item_no: item.item_no || "",
        customer_product_no: item.customer_product_no || "",
        customs_duty_rate: item.customs_duty_rate != null ? String(item.customs_duty_rate) : "",
        tax_rebate_rate: item.tax_rebate_rate != null ? String(item.tax_rebate_rate) : "",
        compression_rate: item.compression_rate != null ? String(item.compression_rate) : "",
      });
      setEntryOverrides(item.entry_overrides || {});
      setPreviewPrices({});
      setPhotoPreview(item.photo_url || null);
      setSimilarSearch("");
      setAutoSavedAt(null);
      // Release the guard on the next tick so subsequent user edits do
      // trigger auto-save.
      setTimeout(() => { initializingRef.current = false; }, 0);
    }
  }, [item]);

  // iter 114: reload the Similar Images grid whenever the search box changes
  // or the dialog opens on a new item. Empty search still queries by item_no
  // prefix (server-side default behaviour of /inventory/similar).
  useEffect(() => {
    if (!open || !item) return;
    const controller = new AbortController();
    const t = setTimeout(async () => {
      try {
        const params = {};
        if (item.item_no) params.item_no = item.item_no;
        if (similarSearch.trim()) params.description = similarSearch.trim();
        const { data } = await api.get("/inventory/similar", { params, signal: controller.signal });
        setSimilarItems(data.items || []);
      } catch {
        /* silent - similar images is supplementary */
      }
    }, 300);
    return () => { clearTimeout(t); controller.abort(); };
  }, [open, item, similarSearch]);

  // iter 192 (2026-02): debounced live-preview of Calculated Price.
  // Re-runs whenever the user edits any field that feeds the pricing
  // formula (customs_duty_rate / tax_rebate_rate / compression_rate at
  // product level, and any per-variant entry override such as
  // purchase_price / carton_length / carton_width / carton_height /
  // pcs_per_ctn / qty_40ft). Calls a no-side-effect POST that returns
  // { prices: { <price_entry_id>: computed_final_price } }. Falls back
  // to `v.price` (persisted, from the last save) until the first
  // preview response arrives.
  useEffect(() => {
    if (!open || !item || !cartId) return;
    const controller = new AbortController();
    const t = setTimeout(async () => {
      try {
        const payload = {
          customs_duty_rate: form.customs_duty_rate === "" ? null : Number(form.customs_duty_rate),
          tax_rebate_rate: form.tax_rebate_rate === "" ? null : Number(form.tax_rebate_rate),
          compression_rate: form.compression_rate === "" ? null : Number(form.compression_rate),
          entry_overrides: entryOverrides,
        };
        // eslint-disable-next-line no-console
        console.log("[QuoteItemOverrideDialog] preview →", payload);
        const { data } = await api.post(
          `/carts/${cartId}/product-overrides/${item.product_id}/preview`,
          payload,
          { signal: controller.signal },
        );
        // eslint-disable-next-line no-console
        console.log("[QuoteItemOverrideDialog] preview ←", data);
        setPreviewPrices(data?.prices || {});
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[QuoteItemOverrideDialog] preview error", e);
      }
    }, 250);
    return () => { clearTimeout(t); controller.abort(); };
  }, [open, item, cartId, form.customs_duty_rate, form.tax_rebate_rate, form.compression_rate, entryOverrides]);

  // iter 239: 防抖自动保存 —— 800ms 内无更改则触发 PUT。initializing 期
  // 间(item 切换时)跳过,dialog 关闭时也不再排队。必须在 `if (!item)
  // return null` 之前,遵守 React rules-of-hooks(hook 调用顺序稳定)。
  useEffect(() => {
    if (!open || !item || initializingRef.current) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      performAutoSaveRef.current?.(false);
    }, 800);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, entryOverrides, open, item]);

  if (!item) return null;

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // iter 114: click a Similar Images thumbnail -> replace this quote's photo
  // with that URL (already served from R2, no re-upload needed).
  const applySimilarPhoto = async (url) => {
    try {
      await api.put(`/carts/${cartId}/product-overrides/${item.product_id}`, { photo_url: url });
      setPhotoPreview(url);
      toast.success("Photo replaced (this quote only)");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Photo replace failed");
    }
  };

  // iter 135: hide records missing packing data. User requirement:
  // 只搜寻有产品装箱数据、长宽高有信息的记录. Require all three of
  // carton_length/width/height to be present and > 0.
  const hasPackingDims = (r) =>
    Number(r?.carton_length) > 0 &&
    Number(r?.carton_width) > 0 &&
    Number(r?.carton_height) > 0;

  // iter 114: open Import-Packing dialog for a specific variant row.
  const openImportPacking = async (entryId) => {
    setImportForEntryId(entryId);
    setImportSearch(item.item_no || "");
    setImportCopyPrice(false);
    setImportOpen(true);
    try {
      const { data } = await api.get("/inventory", { params: { q: item.item_no || "", page_size: 50 } });
      setImportResults((data.items || []).filter(hasPackingDims));
    } catch { setImportResults([]); }
  };

  const runImportSearch = async () => {
    // iter 133: split on comma → { q, size }. "THROW, 50X60" searches
    // for description "THROW" AND size "50X60". Backward compat: no comma
    // = single-field q search as before.
    // iter 135: bump page_size so post-filtering by packing dims still
    // returns enough candidates.
    const raw = importSearch.trim();
    const params = { page_size: 50 };
    if (raw.includes(",")) {
      const [descPart, sizePart] = raw.split(",").map((s) => s.trim());
      if (descPart) params.q = descPart;
      if (sizePart) params.size = sizePart;
    } else {
      params.q = raw;
    }
    try {
      const { data } = await api.get("/inventory", { params });
      setImportResults((data.items || []).filter(hasPackingDims));
    } catch { setImportResults([]); }
  };

  // Apply the picked source's packing dims onto the target variant's
  // entry_overrides. Optionally copy purchase_price too.
  const applyImportedPacking = (src) => {
    if (!importForEntryId) return;
    setEntryOverrides((prev) => {
      const eo = { ...(prev[importForEntryId] || {}) };
      if (src.carton_length != null) eo.carton_length = Number(src.carton_length);
      if (src.carton_width != null) eo.carton_width = Number(src.carton_width);
      if (src.carton_height != null) eo.carton_height = Number(src.carton_height);
      if (src.gross_weight != null) eo.gross_weight = Number(src.gross_weight);
      if (src.pcs_per_ctn != null) eo.pcs_per_ctn = Number(src.pcs_per_ctn);
      if (importCopyPrice && src.purchase_price != null) eo.purchase_price = Number(src.purchase_price);
      return { ...prev, [importForEntryId]: eo };
    });
    toast.success(`Imported packing from ${src.item_no}${importCopyPrice ? " + factory cost" : ""}`);
    setImportOpen(false);
  };

  // iter 113: mutate one field on one variant's override map. Empty string
  // clears the override (falls back to global entry on next render).
  const updateVariant = (entryId, field, rawValue) => {
    setEntryOverrides((prev) => {
      const eo = { ...(prev[entryId] || {}) };
      if (rawValue === "" || rawValue == null) {
        delete eo[field];
      } else {
        // Numeric fields stay numeric; UPC keeps its string.
        const numericFields = ["purchase_price", "carton_length", "carton_width", "carton_height", "gross_weight", "pcs_per_ctn", "qty_40ft"];
        eo[field] = numericFields.includes(field) ? Number(rawValue) : rawValue;
      }
      const next = { ...prev };
      if (Object.keys(eo).length === 0) delete next[entryId];
      else next[entryId] = eo;
      return next;
    });
  };

  // iter 239 (2026-02): 构造 PUT payload —— 与旧的 handleSave 保持一致。
  const buildOverridePayload = () => ({
    description: form.description,
    specification: form.specification,
    chinese_name: form.chinese_name,
    packaging_requirement: form.packaging_requirement,
    quality_requirement: form.quality_requirement,
    remark: form.remark,
    item_no: form.item_no,
    customer_product_no: form.customer_product_no,
    customs_duty_rate: form.customs_duty_rate === "" ? null : Number(form.customs_duty_rate),
    tax_rebate_rate: form.tax_rebate_rate === "" ? null : Number(form.tax_rebate_rate),
    compression_rate: form.compression_rate === "" ? null : Number(form.compression_rate),
    entry_overrides: entryOverrides,
  });

  // iter 239: 静默自动保存。debounced 保存不 refetch 父组件(避免打字
  // 期间列表频繁刷新);flush 时(关闭/切换)才 refetch,让主列表同步。
  const performAutoSave = async (triggerParentRefetch = false) => {
    if (!cartId || !item?.product_id) return;
    const payload = buildOverridePayload();
    latestPayloadRef.current = payload;
    setAutoSaving(true);
    try {
      await api.put(`/carts/${cartId}/product-overrides/${item.product_id}`, payload);
      setAutoSavedAt(new Date());
      if (triggerParentRefetch) onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Auto-save failed");
    } finally {
      setAutoSaving(false);
    }
  };
  // Keep the ref pointing at the latest closure so the auto-save
  // useEffect (declared before the early return) can invoke the most
  // recent function without re-registering the timer on every render.
  performAutoSaveRef.current = performAutoSave;

  // iter 239: 立即 flush —— 关闭 dialog / Prev / Next 时调用,确保
  // 用户刚敲完还未 debounce 触发的更改不丢失,并刷新父列表。
  const flushAutoSave = async () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
      await performAutoSave(true);
    } else {
      // Timer already fired; still refresh parent so list reflects saves.
      onSaved?.();
    }
  };

  // iter 239: 包一层 —— Prev/Next 前先 flush 再切换。
  const handleNavigate = async (nextProduct) => {
    await flushAutoSave();
    onNavigate?.(nextProduct);
  };

  // iter 239: dialog Close 拦截 —— close 时 flush 一次(异步,不阻塞 UI)。
  const handleOpenChange = (openNext) => {
    if (!openNext) {
      // fire-and-forget flush; dialog closes immediately for snappy UX
      flushAutoSave();
    }
    onOpenChange?.(openNext);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        description: form.description,
        specification: form.specification,
        chinese_name: form.chinese_name,
        packaging_requirement: form.packaging_requirement,
        quality_requirement: form.quality_requirement,
        remark: form.remark,
        item_no: form.item_no,
        customer_product_no: form.customer_product_no,
        customs_duty_rate: form.customs_duty_rate === "" ? null : Number(form.customs_duty_rate),
        tax_rebate_rate: form.tax_rebate_rate === "" ? null : Number(form.tax_rebate_rate),
        compression_rate: form.compression_rate === "" ? null : Number(form.compression_rate),
        entry_overrides: entryOverrides,
      };
      await api.put(`/carts/${cartId}/product-overrides/${item.product_id}`, payload);
      toast.success("Quote item updated (this quote only)");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.put(`/carts/${cartId}/product-overrides/${item.product_id}/photo`, fd);
      setPhotoPreview(data.photo_url);
      toast.success("Photo updated (this quote only)");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Photo upload failed");
    } finally {
      setUploadingPhoto(false);
    }
  };

  // iter 122: revert the quote-only photo override so this line falls back
  // to the frozen snapshot photo (i.e. whatever the global product had at
  // add-to-cart time). Sending null via the override PUT clears the field;
  // serialize_cart_item then falls through to product_snapshot.photo_url.
  const handleRestorePhoto = async () => {
    try {
      const { data } = await api.put(`/carts/${cartId}/product-overrides/${item.product_id}`, { photo_url: null });
      // iter 122d: backend returns the effective photo URL after clearing
      // the override (falls back to frozen snapshot / live product). Update
      // the local preview immediately so the user sees the restore without
      // re-opening the dialog. Keep dialog open per iter 122c.
      setPhotoPreview(data?.photo_url || null);
      toast.success("Restored to original photo");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restore failed");
    }
  };

  const handleReset = async () => {
    try {
      await api.delete(`/carts/${cartId}/product-overrides/${item.product_id}`);
      toast.success("Reset to global product data");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    }
  };

  // iter 233 (2026-02): Delete this product (all its variants) from the
  // current quotation. Also removes the product_overrides for a clean slate.
  const handleDeleteProduct = async () => {
    const variants = item?.variants || [];
    const count = variants.length;
    if (count === 0) return;
    const label = item?.description || item?.item_no || "this product";
    if (!window.confirm(`Delete "${label}" and its ${count} variant${count > 1 ? "s" : ""} from this quotation?`)) return;
    try {
      // Delete each variant cart_item; product-overrides get cleaned up
      // automatically on the last item removal (backend behaviour), but we
      // also fire the explicit reset to be safe on partial failures.
      for (const v of variants) {
        await api.delete(`/carts/${cartId}/items/${v.item_id}`);
      }
      toast.success(`Removed ${count} item${count > 1 ? "s" : ""}`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
      onSaved?.();
    }
  };

  // iter 115: open the inline Add-Size form. Clears any previous entries so
  // reopening starts fresh.
  const openAddSizeForm = () => {
    setAddSizeForm({
      size: "", upc: "", purchase_price: "", pcs_per_ctn: "",
      carton_length: "", carton_width: "", carton_height: "",
      gross_weight: "", qty: "",
    });
    setAddSizeOpen(true);
  };

  const submitAddSize = async () => {
    if (!addSizeForm.size.trim()) {
      toast.error("Size is required");
      return;
    }
    setAddSizeSaving(true);
    try {
      // Build a compact body - only include filled numeric fields so the
      // backend seed logic (copy-from-sibling) kicks in for blanks.
      const body = { product_id: item.product_id, size: addSizeForm.size.trim() };
      if (addSizeForm.upc.trim()) body.upc = addSizeForm.upc.trim();
      const numFields = ["purchase_price", "pcs_per_ctn", "carton_length", "carton_width", "carton_height", "gross_weight", "qty"];
      numFields.forEach((f) => {
        if (addSizeForm[f] !== "" && addSizeForm[f] != null) body[f] = Number(addSizeForm[f]);
      });
      await api.post(`/carts/${cartId}/items/add-size`, body);
      toast.success(`Added size "${body.size}" (quote-only)`);
      setAddSizeOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Add size failed");
    } finally {
      setAddSizeSaving(false);
    }
  };

  // iter 140: delete a single variant row from this quote (uses the
  // existing cart item DELETE endpoint - global product data is untouched).
  const handleDeleteVariant = async (variantItemId) => {
    if (!window.confirm("Delete this size from this quote?")) return;
    try {
      await api.delete(`/carts/${cartId}/items/${variantItemId}`);
      toast.success("Size removed from quote");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  // iter 140: reorder variants within this product. `cartItems` is the whole
  // cart in current order; we swap two neighboring rows of this product and
  // POST the full flat order with sequential sort_order values.
  const handleMoveVariant = async (variantItemId, dir) => {
    if (!Array.isArray(cartItems)) return;
    const flat = cartItems.slice();
    // Find variants of this product in flat order
    const productIndices = flat
      .map((ci, i) => (ci.product_id === item?.product_id ? i : -1))
      .filter((i) => i !== -1);
    const localIdx = productIndices.findIndex((i) => flat[i].item_id === variantItemId);
    if (localIdx === -1) return;
    const targetLocal = localIdx + dir;
    if (targetLocal < 0 || targetLocal >= productIndices.length) return;
    const a = productIndices[localIdx];
    const b = productIndices[targetLocal];
    [flat[a], flat[b]] = [flat[b], flat[a]];
    const payload = flat.map((ci, idx) => ({ id: ci.item_id, sort_order: idx }));
    try {
      await api.put(`/carts/${cartId}/items/reorder`, payload);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reorder failed");
    }
  };

  // iter 141: duplicate a variant row. Reuses the add-size endpoint with
  // all source fields copied and size suffixed " (copy)" so the new row
  // is a fresh quote-only price entry, not a reference to the source.
  const handleCopyVariant = async (variant) => {
    if (!variant) return;
    const body = {
      product_id: item.product_id,
      size: `${variant.size || "size"} (copy)`,
    };
    if (variant.upc) body.upc = variant.upc;
    const numFields = ["purchase_price", "pcs_per_ctn", "carton_length", "carton_width", "carton_height", "gross_weight", "qty"];
    numFields.forEach((f) => {
      if (variant[f] != null && variant[f] !== "") body[f] = Number(variant[f]);
    });
    try {
      await api.post(`/carts/${cartId}/items/add-size`, body);
      toast.success(`Copied "${variant.size || ""}"`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Copy failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* iter 227 (2026-02): 紧凑重设计。
          - DialogContent 拉宽到 [1200px],高度封顶 92vh + overflow-hidden + flex-col,
            让内部段落用 flex 收缩,避免整页滚动条。Header/Footer 固定,中间内容按需
            自适应。
          - 左侧图片列缩到 120px,Similar Photos 只保留 3 张缩略图和 View All 链接。
          - 中间新增 Packaging / Quality Requirement 两个紧凑 textarea(rows=2)。
          - Price Entries 表整体 Excel 风格:12px 字号,细网格线,cell padding 极小,
            无 rounded 无 shadow;头部 Duty/Rebate/压缩率 inline 排列,不再单独占一行。 */}
      <DialogContent className="rounded-xl max-w-[95vw] sm:max-w-[960px] w-[960px] max-h-[92vh] p-4 gap-2 overflow-hidden flex flex-col" data-testid="quote-item-override-dialog">
        <DialogHeader className="pb-1">
          {/* iter 230 (2026-02): 标题栏右侧的 Prev/Next 按钮 —— 让用户不
              关闭 Dialog 就能翻看 productList 里的上/下一个产品。索引显示
              在中间(1 / N)。到边界时按钮 disabled。 */}
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="text-base">Edit Quote Item</DialogTitle>
              <p className="text-[10px] text-gray-400">Changes here apply only to this quotation. Global product data and other quotations remain untouched.</p>
            </div>
            {(() => {
              if (!Array.isArray(productList) || productList.length === 0 || !item) return null;
              const curIdx = productList.findIndex((p) => p.product_id === item.product_id);
              const hasPrev = curIdx > 0;
              const hasNext = curIdx >= 0 && curIdx < productList.length - 1;
              return (
                <div className="flex items-center gap-1 flex-shrink-0 mr-8" data-testid="override-nav-toolbar">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => hasPrev && handleNavigate(productList[curIdx - 1])}
                    disabled={!hasPrev}
                    className="h-7 w-8 px-0 rounded-md"
                    title="Previous product"
                    data-testid="override-nav-prev"
                  >
                    <ChevronLeft size={14} />
                  </Button>
                  <span className="font-mono text-[11px] text-gray-500 tabular-nums select-none min-w-[46px] text-center" data-testid="override-nav-position">
                    {curIdx >= 0 ? `${curIdx + 1} / ${productList.length}` : `- / ${productList.length}`}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => hasNext && handleNavigate(productList[curIdx + 1])}
                    disabled={!hasNext}
                    className="h-7 w-8 px-0 rounded-md"
                    title="Next product"
                    data-testid="override-nav-next"
                  >
                    <ChevronRight size={14} />
                  </Button>
                </div>
              );
            })()}
          </div>
        </DialogHeader>

        <div className="grid grid-cols-[120px_1fr] gap-3 flex-shrink-0">
          {/* Left: Photo */}
          <div>
            <div>
              {photoPreview ? (
                <img src={photoPreview} alt="" className="w-full aspect-square object-contain rounded-md border border-gray-200 bg-white" data-testid="override-photo-preview" />
              ) : (
                <div className="w-full aspect-square bg-gray-50 rounded-md border border-gray-200 flex items-center justify-center text-[10px] text-gray-400">No Image</div>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoFileSelected} className="hidden" data-testid="override-photo-file-input" />
              <Button
                type="button"
                variant="outline"
                disabled={uploadingPhoto}
                onClick={() => fileInputRef.current?.click()}
                className="w-full mt-1.5 rounded-md border-gray-300 h-7 text-[11px]"
                data-testid="override-replace-photo-button"
              >
                <ImageUp size={12} className="mr-1" /> {uploadingPhoto ? "Uploading..." : "Upload"}
              </Button>
              {item.is_overridden && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleRestorePhoto}
                  className="w-full rounded-md text-gray-500 hover:text-brand-600 text-[10px] h-6"
                  data-testid="override-restore-photo-button"
                >
                  Restore Original
                </Button>
              )}
            </div>

            {/* iter 114: Similar Photos — 紧凑版 */}
            <div className="mt-2">
              <div className="flex items-center justify-between">
                <Label className="text-[10px] font-medium text-gray-500">Similar ({similarItems.length})</Label>
                {similarItems.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setShowAllSimilar((v) => !v)}
                    className="text-[10px] text-brand-600 hover:text-brand-700"
                    data-testid="override-similar-toggle"
                  >
                    {showAllSimilar ? "▲" : "View All ▾"}
                  </button>
                )}
              </div>
              <Input
                value={similarSearch}
                onChange={(e) => setSimilarSearch(e.target.value)}
                placeholder="Search Item No"
                className="mt-1 rounded-md border-gray-300 text-[10px] h-6 px-1.5"
                data-testid="override-similar-search"
              />
              <div className="grid grid-cols-3 gap-1 mt-1" data-testid="override-similar-grid">
                {(showAllSimilar ? similarItems : similarItems.slice(0, 6)).map((s) => (
                  <button
                    key={s.entry_id || s.item_no}
                    type="button"
                    onClick={() => applySimilarPhoto(s.photo_url)}
                    disabled={!s.photo_url}
                    title={`${s.item_no || ""}\n${s.description || ""}`}
                    className="aspect-square rounded border border-gray-200 overflow-hidden hover:ring-1 hover:ring-brand-400 hover:border-brand-400 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`override-similar-thumb-${s.entry_id || s.item_no}`}
                  >
                    {s.photo_url
                      ? <img src={s.photo_url} alt={s.item_no || ""} className="w-full h-full object-cover" />
                      : <div className="w-full h-full bg-gray-100 flex items-center justify-center text-[8px] text-gray-400">-</div>}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Middle: 产品编号 / 产品描述 / 规格 & 材料 / 中文 / 包装要求 / 品质要求 */}
          <div className="flex flex-col gap-2">
            {/* iter 242 (2026-02): Company Code / Customer Code 用户觉得
                原来占满一整格太宽了 —— 改成固定 w-44 的窄输入并列显示,
                左对齐,右侧留空。 */}
            <div className="flex items-start gap-3">
              <div className="w-44">
                <Label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Company Code</Label>
                <Input
                  value={form.item_no}
                  onChange={(e) => update("item_no", e.target.value)}
                  className="mt-0.5 rounded-md border-gray-300 h-7 text-xs focus-visible:ring-brand-500 font-mono"
                  data-testid="override-item-no-input"
                />
              </div>
              <div className="w-44">
                <Label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Customer Code</Label>
                <Input
                  value={form.customer_product_no}
                  onChange={(e) => update("customer_product_no", e.target.value)}
                  className="mt-0.5 rounded-md border-gray-300 h-7 text-xs focus-visible:ring-brand-500 font-mono"
                  data-testid="override-customer-product-no-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <Label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Description</Label>
                <textarea
                  value={form.description}
                  onChange={(e) => update("description", e.target.value)}
                  rows={2}
                  className="mt-0.5 w-full rounded-md border border-gray-300 text-xs px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                  data-testid="override-description-input"
                />
              </div>
              <div>
                <Label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Specification &amp; Material</Label>
                <textarea
                  value={form.specification}
                  onChange={(e) => update("specification", e.target.value)}
                  rows={2}
                  className="mt-0.5 w-full rounded-md border border-gray-300 text-xs px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                  data-testid="override-specification-input"
                />
              </div>
              <div>
                <Label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Chinese Description</Label>
                <textarea
                  value={form.chinese_name}
                  onChange={(e) => update("chinese_name", e.target.value)}
                  rows={2}
                  className="mt-0.5 w-full rounded-md border border-gray-300 text-xs px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                  data-testid="override-chinese-name-input"
                />
              </div>
              {/* iter 227 (2026-02): 包装要求 / 品质要求 quote-only overrides.
                  iter 242 (2026-02): 高度加倍(rows=4)—— 用户反馈现有可视区
                  太矮,大段包装/品质描述看不到全文。 */}
              <div>
                <Label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">包装要求 · Packaging</Label>
                <textarea
                  value={form.packaging_requirement}
                  onChange={(e) => update("packaging_requirement", e.target.value)}
                  rows={4}
                  className="mt-0.5 w-full rounded-md border border-gray-300 text-xs px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                  data-testid="override-packaging-requirement-input"
                />
              </div>
              <div>
                <Label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">品质要求 · Quality</Label>
                <textarea
                  value={form.quality_requirement}
                  onChange={(e) => update("quality_requirement", e.target.value)}
                  rows={4}
                  className="mt-0.5 w-full rounded-md border border-gray-300 text-xs px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                  data-testid="override-quality-requirement-input"
                />
              </div>
            </div>
            <div className="mt-2">
              <Label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">备注 · Remark</Label>
              <textarea
                value={form.remark}
                onChange={(e) => update("remark", e.target.value)}
                rows={2}
                className="mt-0.5 w-full rounded-md border border-gray-300 text-xs px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                data-testid="override-remark-input"
              />
            </div>
          </div>
        </div>

        {/* Variants table (read-only, iter 112 phase 1) */}
        {/* iter 227 (2026-02): Excel 风格价格表 —— 12px 字号,细网格线,cell 极小
            padding,无 rounded。整段 flex-1 min-h-0,让表格内部滚动而不是整个 Dialog. */}
        <div className="mt-2 flex flex-col gap-1">
          <div className="flex items-center justify-between mb-1 flex-shrink-0">
            <h3 className="text-xs font-semibold text-gray-700">Price Entries <span className="text-[10px] font-normal text-gray-400">(one row per size · quote-only editable)</span></h3>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <Label className="text-[10px] font-medium text-gray-500 whitespace-nowrap">Duty (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.customs_duty_rate}
                  onChange={(e) => update("customs_duty_rate", e.target.value)}
                  placeholder="0.00"
                  className="w-16 h-7 rounded-md border-gray-300 focus-visible:ring-brand-500 font-mono text-right text-[11px]"
                  data-testid="override-duty-rate-input"
                />
              </div>
              <div className="flex items-center gap-1">
                <Label className="text-[10px] font-medium text-gray-500 whitespace-nowrap">Rebate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.tax_rebate_rate}
                  onChange={(e) => update("tax_rebate_rate", e.target.value)}
                  placeholder="0.00"
                  className="w-16 h-7 rounded-md border-gray-300 focus-visible:ring-brand-500 font-mono text-right text-[11px]"
                  data-testid="override-tax-rebate-rate-input"
                />
              </div>
              <div className="flex items-center gap-1">
                <Label className="text-[10px] font-medium text-gray-500 whitespace-nowrap" title="Divides auto-computed 40'HQ qty. e.g. 0.5 → 2×; blank = disabled">压缩率</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.compression_rate}
                  onChange={(e) => update("compression_rate", e.target.value)}
                  placeholder="1.00"
                  className="w-16 h-7 rounded-md border-gray-300 focus-visible:ring-brand-500 font-mono text-right text-[11px]"
                  data-testid="override-compression-rate-input"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={openAddSizeForm}
                className="rounded-md border-brand-300 text-brand-700 hover:bg-brand-50 h-7 text-[11px]"
                data-testid="override-add-size-button"
              >
                + Add Size
              </Button>
            </div>
          </div>

          {/* iter 140: Add-Size form moved ABOVE the table so it's
              instantly visible when the button is clicked. Was previously
              rendered under the hint below and users thought the button
              did nothing (form was scrolled off-screen). */}
          {addSizeOpen && (
            <div className="mb-3 border border-brand-200 rounded-lg p-4 bg-brand-50/40" data-testid="override-add-size-form">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-brand-800">New Size Details <span className="text-xs font-normal text-gray-500">(quote-only)</span></h4>
                <button type="button" onClick={() => setAddSizeOpen(false)} className="text-xs text-gray-500 hover:text-red-600" data-testid="override-add-size-cancel-icon">✕</button>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Size *</Label>
                  <Input value={addSizeForm.size} onChange={(e) => setAddSizeForm((f) => ({ ...f, size: e.target.value }))} placeholder="e.g. 90x180" className="mt-1 h-8 text-xs rounded-md" data-testid="add-size-input-size" />
                </div>
                <div>
                  <Label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">UPC / Barcode</Label>
                  <Input value={addSizeForm.upc} onChange={(e) => setAddSizeForm((f) => ({ ...f, upc: e.target.value }))} placeholder="optional" className="mt-1 h-8 text-xs rounded-md font-mono" data-testid="add-size-input-upc" />
                </div>
                <div>
                  <Label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Factory Cost (¥)</Label>
                  <Input type="number" step="0.01" value={addSizeForm.purchase_price} onChange={(e) => setAddSizeForm((f) => ({ ...f, purchase_price: e.target.value }))} className="mt-1 h-8 text-xs rounded-md font-mono" data-testid="add-size-input-purchase-price" />
                </div>
                <div>
                  <Label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">PCS / CTN</Label>
                  <Input type="number" step="1" value={addSizeForm.pcs_per_ctn} onChange={(e) => setAddSizeForm((f) => ({ ...f, pcs_per_ctn: e.target.value }))} className="mt-1 h-8 text-xs rounded-md font-mono" data-testid="add-size-input-pcs" />
                </div>
                <div>
                  <Label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Carton L (CM)</Label>
                  <Input type="number" step="0.1" value={addSizeForm.carton_length} onChange={(e) => setAddSizeForm((f) => ({ ...f, carton_length: e.target.value }))} className="mt-1 h-8 text-xs rounded-md font-mono" data-testid="add-size-input-carton-l" />
                </div>
                <div>
                  <Label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Carton W (CM)</Label>
                  <Input type="number" step="0.1" value={addSizeForm.carton_width} onChange={(e) => setAddSizeForm((f) => ({ ...f, carton_width: e.target.value }))} className="mt-1 h-8 text-xs rounded-md font-mono" data-testid="add-size-input-carton-w" />
                </div>
                <div>
                  <Label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Carton H (CM)</Label>
                  <Input type="number" step="0.1" value={addSizeForm.carton_height} onChange={(e) => setAddSizeForm((f) => ({ ...f, carton_height: e.target.value }))} className="mt-1 h-8 text-xs rounded-md font-mono" data-testid="add-size-input-carton-h" />
                </div>
                <div>
                  <Label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Gross Wt (KG)</Label>
                  <Input type="number" step="0.01" value={addSizeForm.gross_weight} onChange={(e) => setAddSizeForm((f) => ({ ...f, gross_weight: e.target.value }))} className="mt-1 h-8 text-xs rounded-md font-mono" data-testid="add-size-input-gross" />
                </div>
                <div>
                  <Label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Order Qty</Label>
                  <Input type="number" step="1" value={addSizeForm.qty} onChange={(e) => setAddSizeForm((f) => ({ ...f, qty: e.target.value }))} className="mt-1 h-8 text-xs rounded-md font-mono" data-testid="add-size-input-qty" />
                </div>
              </div>
              <p className="text-[10px] text-gray-500 mt-3">
                Only <span className="font-semibold">Size</span> is required. Blank fields will inherit values from an existing variant so the 40ft container calc still works.
              </p>
              <div className="flex justify-end gap-2 mt-3">
                <Button type="button" variant="ghost" size="sm" onClick={() => setAddSizeOpen(false)} disabled={addSizeSaving} className="text-gray-600 h-8 text-xs" data-testid="add-size-cancel-button">
                  Cancel
                </Button>
                <Button type="button" size="sm" onClick={submitAddSize} disabled={addSizeSaving || !addSizeForm.size.trim()} className="rounded-md bg-brand-600 hover:bg-brand-700 h-8 text-xs" data-testid="add-size-save-button">
                  {addSizeSaving ? "Adding..." : "Add Size"}
                </Button>
              </div>
            </div>
          )}
          {/* iter 229 (2026-02): 价格表去掉 flex-1 min-h-0 overflow-auto,
              让表格按内容自然高度渲染 —— 不出内部滚动条,前 4 行完整显示;
              更多行时超出 Dialog 垂直区,极端场景可考虑再加分页,但通常
              一个产品的尺码在 4-5 行以内。 */}
          <div className="border border-gray-300 flex-shrink-0 [&_table]:text-[11px] [&_th]:!h-7 [&_th]:!px-1.5 [&_th]:!py-0.5 [&_th]:!bg-gray-100 [&_th]:!text-gray-700 [&_th]:!font-semibold [&_th]:!border-r [&_th]:!border-gray-300 [&_th]:!leading-tight [&_th:last-child]:!border-r-0 [&_td]:!p-0.5 [&_td]:!border-r [&_td]:!border-gray-200 [&_td]:!leading-tight [&_td]:!align-middle [&_td:last-child]:!border-r-0 [&_tr]:!border-b [&_tr]:!border-gray-200 [&_tr:hover]:!bg-blue-50/30 [&_input]:!h-6 [&_input]:!text-[11px] [&_input]:!rounded-none [&_input]:!border-0 [&_input]:!shadow-none [&_input]:!bg-transparent [&_input]:!px-1 [&_input]:focus:!bg-white [&_input]:focus:!ring-1 [&_input]:focus:!ring-brand-400">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50 border-b border-gray-200">
                  <TableHead className="text-xs uppercase tracking-label text-gray-500 w-10 text-center">#</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500">Size</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500">UPC / Barcode</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500 text-right">Calculated Price</TableHead>
                  {/* iter 228 (2026-02): Final Price 编辑列(cart_item.final_price) */}
                  <TableHead className="text-xs uppercase tracking-label text-gray-500 text-right">Final Price</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500 text-right">Factory Cost (¥)</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500 text-right">40ft Qty</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500 text-center">Carton (CM)</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500 text-right">Gross (KG)</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500 text-right">PCS/CTN</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500 text-right w-20">Sample</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500 text-right w-28">Reserve CI</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500 text-center w-10">Import</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500 text-center w-28">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="override-variants-tbody">
                {productVariants.length === 0 ? (
                  <TableRow><TableCell colSpan={12} className="text-center text-xs text-gray-400 py-4">No variants on this quote.</TableCell></TableRow>
                ) : (
                  productVariants.map((v, vIdx) => {
                    // iter 113: Show current effective value (server-side merged)
                    // in the input; local pending edits sit in entryOverrides
                    // until Save. When a value's edited, use the pending
                    // number/string in state.
                    const eid = v.price_entry_id;
                    const eo = entryOverrides[eid] || {};
                    const val = (field) => (field in eo ? eo[field] : (v[field] ?? ""));
                    const L = "carton_length" in eo ? eo.carton_length : v.carton_length;
                    const W = "carton_width" in eo ? eo.carton_width : v.carton_width;
                    const H = "carton_height" in eo ? eo.carton_height : v.carton_height;
                    const pcs = "pcs_per_ctn" in eo ? eo.pcs_per_ctn : v.pcs_per_ctn;
                    // iter 185: apply product-level compression_rate as a
                    // divisor on the auto-computed qty (mirrors backend
                    // logic in _compute_qty_40ft_override).
                    const rawQty40 = L && W && H && pcs ? (65_000_000 * pcs) / (L * W * H) : null;
                    const cr = form.compression_rate !== "" ? Number(form.compression_rate) : null;
                    const qty40 = rawQty40 != null
                      ? Math.floor(cr && cr !== 1 ? rawQty40 / cr : rawQty40)
                      : null;
                    const cellCls = "font-mono text-xs h-6 text-right";
                    return (
                      <TableRow key={v.item_id} className="border-b border-gray-100" data-testid={`override-variant-row-${v.item_id}`}>
                        <TableCell className="text-xs text-gray-500 text-center">{vIdx + 1}</TableCell>
                        {/* iter 232 (2026-02): SIZE 也可编辑,走 entry_overrides,
                            backend 用 `{**entry, **entry_ov}` 通用合并,无需额外接口。 */}
                        <TableCell className="p-1">
                          <Input value={val("size")} onChange={(e) => updateVariant(eid, "size", e.target.value)} className={`${cellCls} text-left w-20`} data-testid={`override-variant-size-${eid}`} />
                        </TableCell>
                        <TableCell className="p-1">
                          <Input value={val("upc")} onChange={(e) => updateVariant(eid, "upc", e.target.value)} className={`${cellCls} text-left w-24`} data-testid={`override-variant-upc-${eid}`} />
                        </TableCell>
                        <TableCell className="font-mono text-sm text-right text-brand-700 font-semibold" data-testid={`override-variant-price-${eid}`}>{(() => {
                          const p = eid in previewPrices ? previewPrices[eid] : v.price;
                          return p != null ? Number(p).toFixed(3) : "-";
                        })()}</TableCell>
                        {/* iter 228 (2026-02): Final Price 输入 —— 每格失焦直接 PUT
                            cart_items/{item_id},保留手工价 (逃出 formula 自动重算)。 */}
                        <TableCell className="p-1">
                          <Input
                            type="number"
                            step="0.001"
                            defaultValue={v.final_price != null ? Number(v.final_price).toFixed(3) : ""}
                            placeholder={(() => {
                              const p = eid in previewPrices ? previewPrices[eid] : v.price;
                              return p != null ? Number(p).toFixed(3) : "";
                            })()}
                            onBlur={async (e) => {
                              const raw = e.target.value;
                              const val2 = raw === "" ? null : Number(raw);
                              if (val2 === v.final_price) return;
                              try {
                                await api.put(`/carts/${cartId}/items/${v.item_id}`, { final_price: val2 });
                                if (onSaved) await onSaved();
                                toast.success("Final price updated");
                              } catch {
                                toast.error("Update failed");
                              }
                            }}
                            className={`${cellCls} w-20 !text-emerald-700 !font-semibold`}
                            data-testid={`override-variant-final-price-${eid}`}
                          />
                        </TableCell>
                        <TableCell className="p-1">
                          <Input type="number" step="0.01" value={val("purchase_price")} onChange={(e) => updateVariant(eid, "purchase_price", e.target.value)} className={`${cellCls} w-16`} data-testid={`override-variant-purchase-price-${eid}`} />
                        </TableCell>
                        {/* iter 139: 40尺柜装箱量 is now user-editable. If the
                            user sets a value, it wins; blank falls back to
                            the auto-computed value (shown as placeholder). */}
                        <TableCell className="p-1">
                          <Input
                            type="number"
                            step="1"
                            value={"qty_40ft" in eo ? eo.qty_40ft : ""}
                            placeholder={qty40 != null ? qty40.toLocaleString() : ""}
                            onChange={(e) => updateVariant(eid, "qty_40ft", e.target.value)}
                            className={`${cellCls} w-16`}
                            data-testid={`override-variant-qty-40ft-${eid}`}
                          />
                        </TableCell>
                        {/* iter 144: combine L / W / H into a single 规格 (CM) cell (per mockup). */}
                        <TableCell className="p-1">
                          <div className="flex items-center gap-0.5 justify-center">
                            <Input type="number" step="0.1" value={val("carton_length")} onChange={(e) => updateVariant(eid, "carton_length", e.target.value)} className={`${cellCls} w-11 px-1`} data-testid={`override-variant-carton-l-${eid}`} />
                            <span className="text-[9px] text-gray-400">×</span>
                            <Input type="number" step="0.1" value={val("carton_width")} onChange={(e) => updateVariant(eid, "carton_width", e.target.value)} className={`${cellCls} w-11 px-1`} data-testid={`override-variant-carton-w-${eid}`} />
                            <span className="text-[9px] text-gray-400">×</span>
                            <Input type="number" step="0.1" value={val("carton_height")} onChange={(e) => updateVariant(eid, "carton_height", e.target.value)} className={`${cellCls} w-11 px-1`} data-testid={`override-variant-carton-h-${eid}`} />
                          </div>
                        </TableCell>
                        <TableCell className="p-1">
                          <Input type="number" step="0.01" value={val("gross_weight")} onChange={(e) => updateVariant(eid, "gross_weight", e.target.value)} className={`${cellCls} w-14`} data-testid={`override-variant-gross-${eid}`} />
                        </TableCell>
                        <TableCell className="p-1">
                          <Input type="number" step="1" value={val("pcs_per_ctn")} onChange={(e) => updateVariant(eid, "pcs_per_ctn", e.target.value)} className={`${cellCls} w-12`} data-testid={`override-variant-pcs-${eid}`} />
                        </TableCell>
                        {/* iter 267: 样品数量 (cart_item.sample_qty),失焦直接保存 */}
                        <TableCell className="p-1">
                          <Input type="number" min="0" defaultValue={v.sample_qty || ""} placeholder="0" data-testid={`override-variant-sample-${eid}`}
                            onBlur={async (e) => { const n = e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0); if (n === (v.sample_qty || 0)) return; try { await api.put(`/carts/${cartId}/items/${v.item_id}`, { sample_qty: n }); onSaved && onSaved(); } catch { toast.error("Save failed"); } }}
                            className={`${cellCls} w-16`} />
                        </TableCell>
                        {/* iter 277: 预留 CI 数量 (ci_reservations),失焦保存;"按订单数量预留"一键按钮 */}
                        <TableCell className="p-1" data-testid={`override-variant-reserve-cell-${eid}`}>
                          <div className="flex items-center gap-1 justify-end">
                            <Input type="number" min="0" key={`rsv-${eid}-${v.ci_reserved_qty || 0}`} defaultValue={v.ci_reserved_qty || ""} placeholder="0" data-testid={`override-variant-reserve-${eid}`} disabled={v.ci_inventory_qty == null}
                              title={v.ci_available != null ? `CI ${v.ci_inventory_qty ?? "-"} · available ${v.ci_available}` : ""}
                              onBlur={async (e) => { const n = e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0); if (n === (v.ci_reserved_qty || 0)) return; try { await api.put(`/carts/${cartId}/items/${v.item_id}/reserve`, { qty: n }); toast.success(n ? `Reserved ${n}` : "Reservation released"); onSaved && onSaved(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reserve failed"); e.target.value = v.ci_reserved_qty || ""; } }}
                              className={`${cellCls} w-16`} />
                            <button type="button" title="Reserve = order qty" data-testid={`override-variant-reserve-order-${eid}`}
                              onClick={async () => { const n = (Number(v.carton_qty) || 0) * (Number(val("pcs_per_ctn")) || 0); if (!n) { toast.error("Order qty is 0"); return; } try { await api.put(`/carts/${cartId}/items/${v.item_id}/reserve`, { qty: n }); toast.success(`Reserved ${n}`); onSaved && onSaved(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reserve failed"); } }}
                              className="h-7 px-1.5 rounded border border-gray-300 text-[10px] text-gray-600 hover:bg-gray-50 whitespace-nowrap">=Qty</button>
                          </div>
                          <p className="text-[10px] text-gray-600 text-right pr-1" data-testid={`override-variant-ci-avail-${eid}`}>CI {v.ci_inventory_qty ?? "-"} · {v.ci_available ?? "-"} avail</p>
                        </TableCell>
                        <TableCell className="p-1 text-center">
                          <button
                            type="button"
                            onClick={() => openImportPacking(eid)}
                            title="Import packing data from another product"
                            className="p-1.5 rounded-md hover:bg-brand-50 text-gray-400 hover:text-brand-600 transition-colors"
                            data-testid={`override-import-packing-${eid}`}
                          >
                            <PackageOpen size={14} strokeWidth={1.75} />
                          </button>
                        </TableCell>
                        {/* iter 140: per-variant move up/down + delete. */}
                        <TableCell className="p-1">
                          <div className="flex items-center justify-center gap-0.5">
                            <button
                              type="button"
                              onClick={() => handleMoveVariant(v.item_id, -1)}
                              disabled={vIdx === 0}
                              title="Move up"
                              className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-brand-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              data-testid={`override-variant-up-${v.item_id}`}
                            >
                              <ChevronUp size={13} strokeWidth={2} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMoveVariant(v.item_id, 1)}
                              disabled={vIdx === productVariants.length - 1}
                              title="Move down"
                              className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-brand-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              data-testid={`override-variant-down-${v.item_id}`}
                            >
                              <ChevronDown size={13} strokeWidth={2} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCopyVariant(v)}
                              title="Copy this size"
                              className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-brand-600 transition-colors ml-0.5"
                              data-testid={`override-variant-copy-${v.item_id}`}
                            >
                              <Copy size={13} strokeWidth={1.75} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteVariant(v.item_id)}
                              title="Delete this size from this quote"
                              className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors ml-0.5"
                              data-testid={`override-variant-delete-${v.item_id}`}
                            >
                              <Trash2 size={13} strokeWidth={1.75} />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <DialogFooter className="flex items-center sm:justify-between gap-2 pt-2 border-t border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            {item.is_overridden && (
              <Button type="button" variant="ghost" onClick={handleReset} className="text-gray-500 hover:text-red-600 rounded-lg" data-testid="override-reset-button">
                <RotateCcw size={13} className="mr-1.5" /> Reset to Global
              </Button>
            )}
            {/* iter 233 (2026-02): Delete Product 按钮 —— 从当前报价里
                批量移除该产品的所有 variant,confirm 后执行。 */}
            <Button type="button" variant="ghost" onClick={handleDeleteProduct} className="text-gray-500 hover:text-red-600 rounded-lg" data-testid="override-delete-product-button">
              <Trash2 size={13} className="mr-1.5" /> Delete Product
            </Button>
          </div>
          <div className="flex items-center gap-3">
            {/* iter 239 (2026-02): 自动保存状态指示器 —— 替换旧的手动 Save
                按钮。saving 时显示"Saving...";空闲时显示上次保存时间
                (或"All changes auto-saved");错误由 toast 承担。 */}
            <span
              className="text-[11px] text-gray-500 tabular-nums select-none flex items-center gap-1"
              data-testid="override-autosave-status"
            >
              {autoSaving ? (
                <>
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                  Saving…
                </>
              ) : autoSavedAt ? (
                <>
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Auto-saved {autoSavedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </>
              ) : (
                <>
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-300" />
                  Auto-save on
                </>
              )}
            </span>
            <Button
              type="button"
              onClick={() => handleOpenChange(false)}
              className="rounded-lg bg-brand-600 hover:bg-brand-700"
              data-testid="override-close-button"
            >
              <Check size={14} className="mr-1.5" /> Close
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>

      {/* iter 114 · 导入订单产品资料 sub-dialog. Search another product on
          this app, pick one, apply its packing dims (optionally its
          purchase_price) into the currently-focused variant row. */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="rounded-xl sm:max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="import-packing-dialog">
          <DialogHeader>
            <DialogTitle>Import Packing Data</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 -mt-2">
            Search a source product; its carton dimensions, gross weight and pcs/carton will apply to this variant only.
            {" "}Only records with L×W×H filled are shown.
            {" "}Tip: <span className="font-medium text-gray-700">"THROW, 50X60"</span> filters by description AND size (comma separated).
          </p>
          <div className="flex items-center gap-2 pt-2">
            <Input
              value={importSearch}
              onChange={(e) => setImportSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runImportSearch()}
              placeholder="Description / Item No [, size]"
              className="rounded-lg border-gray-300 text-sm"
              data-testid="import-packing-search-input"
            />
            <Button type="button" onClick={runImportSearch} className="rounded-lg" data-testid="import-packing-search-button">Search</Button>
          </div>
          <label className="flex items-center gap-2 mt-3 text-xs text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={importCopyPrice}
              onChange={(e) => setImportCopyPrice(e.target.checked)}
              className="rounded border-gray-300"
              data-testid="import-packing-copy-price"
            />
            Also copy purchase price into Factory Cost
          </label>
          <div className="mt-3 border border-gray-200 rounded-lg overflow-y-auto max-h-96" data-testid="import-packing-results">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50">
                  <TableHead className="text-xs">Item / Size</TableHead>
                  <TableHead className="text-xs">Description</TableHead>
                  <TableHead className="text-xs text-right">L×W×H</TableHead>
                  <TableHead className="text-xs text-right">Gross</TableHead>
                  <TableHead className="text-xs text-right">Pcs/Ctn</TableHead>
                  <TableHead className="text-xs text-right">Purch ¥</TableHead>
                  <TableHead className="text-xs text-center w-16">Pick</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {importResults.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-xs text-gray-400 py-4">No results — try another keyword</TableCell></TableRow>
                ) : (
                  importResults.map((r) => (
                    <TableRow key={r.entry_id} className="hover:bg-brand-50/30" data-testid={`import-packing-row-${r.entry_id}`}>
                      <TableCell className="font-mono text-xs">{r.item_no}<span className="text-gray-400 ml-1">/{r.size || "-"}</span></TableCell>
                      <TableCell className="text-xs text-gray-600 max-w-[200px] truncate" title={r.description}>{r.description || "-"}</TableCell>
                      <TableCell className="font-mono text-xs text-right">{r.carton_length ?? "-"}×{r.carton_width ?? "-"}×{r.carton_height ?? "-"}</TableCell>
                      <TableCell className="font-mono text-xs text-right">{r.gross_weight != null ? Number(r.gross_weight).toFixed(2) : "-"}</TableCell>
                      <TableCell className="font-mono text-xs text-right">{r.pcs_per_ctn ?? "-"}</TableCell>
                      <TableCell className="font-mono text-xs text-right text-gray-700">{r.purchase_price != null ? Number(r.purchase_price).toFixed(2) : "-"}</TableCell>
                      <TableCell className="text-center">
                        <Button type="button" size="sm" variant="outline" onClick={() => applyImportedPacking(r)} className="h-7 text-xs" data-testid={`import-packing-apply-${r.entry_id}`}>Import</Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
